import { FC, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { ReactComponent as FacebookIcon } from 'src/assets/svg/facebook.svg'
import { ReactComponent as InstagramIcon } from 'src/assets/svg/instagram.svg'
import { ReactComponent as LinkedinIcon } from 'src/assets/svg/linkedin.svg'
import { ReactComponent as TiktokIcon } from 'src/assets/svg/tiktok.svg'
import { ReactComponent as YoutubeIcon } from 'src/assets/svg/youtube.svg'
import { useDisableScroll } from 'src/hooks/useDisableScroll'
import { AnalysisDraft } from 'src/localStorage/contentAnalysisDraft'
import { CampaignCategoryPicker } from 'src/components/CampaignCategoryPicker'
import classes from './analysisReviewModal.module.scss'

interface AnalysisReviewModalProps {
  draft: AnalysisDraft
  isOpen: boolean
  isSubmitting?: boolean
  onCategoryChange?: (category: string) => void
  onClear: (type: 'recentPosts' | 'specificPosts' | 'captionSearch') => void
  onClearAll: () => void
  onClose: () => void
  onEdit: (type: 'recentPosts' | 'specificPosts' | 'captionSearch') => void
  onSubmit: () => void
}

const getPlatformIcon = (platform: string) => {
  switch (platform) {
    case 'instagram':
      return <InstagramIcon className={classes.platformIcon} />
    case 'facebook':
      return <FacebookIcon className={classes.platformIcon} />
    case 'tiktok':
      return <TiktokIcon className={classes.platformIcon} />
    case 'youtube':
      return <YoutubeIcon className={classes.platformIcon} />
    case 'linkedin':
      return <LinkedinIcon className={classes.platformIcon} />
    default:
      return (
        <svg
          className={classes.platformIcon}
          fill="none"
          height="16"
          viewBox="0 0 24 24"
          width="16"
        >
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="1.5"
          />
        </svg>
      )
  }
}

export const AnalysisReviewModal: FC<AnalysisReviewModalProps> = ({
  draft,
  isOpen,
  isSubmitting = false,
  onCategoryChange,
  onClear,
  onClearAll,
  onClose,
  onEdit,
  onSubmit,
}) => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  useDisableScroll(isOpen)

  if (!isOpen) return null

  const hasAnyData =
    draft.recentPosts || draft.specificPosts || draft.captionSearch
  const canSubmit =
    hasAnyData && (draft.title || '').trim().length > 0 && !isSubmitting

  return createPortal(
    <div className={classes.modalOverlay}>
      <div className={classes.modalContent} ref={ref}>
        <div className={classes.modalHeader}>
          <h2 className={classes.modalTitle}>Review Analysis</h2>
          <button
            className={classes.closeButton}
            onClick={onClose}
            type="button"
          >
            <svg fill="none" height="24" viewBox="0 0 24 24" width="24">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
              />
            </svg>
          </button>
        </div>

        <div className={classes.modalBody}>
          {/* Title Input */}
          <div className={classes.titleSection}>
            <div className={classes.titleHeader}>
              <label className={classes.titleLabel} htmlFor="analysis-title">
                Analysis Title <span className={classes.required}>*</span>
              </label>
              <button
                className={classes.editTitleButton}
                onClick={() => {
                  if (draft.recentPosts) {
                    onEdit('recentPosts')
                  } else if (draft.specificPosts) {
                    onEdit('specificPosts')
                  } else if (draft.captionSearch) {
                    onEdit('captionSearch')
                  }
                }}
                type="button"
              >
                Edit
              </button>
            </div>
            <div className={classes.titleDisplay}>
              {draft.title || 'No title set'}
            </div>
          </div>
          {/* Campaign Category */}
          {onCategoryChange && (
            <div className={classes.categorySection}>
              <CampaignCategoryPicker
                exclude={['Review Analysis']}
                onChange={onCategoryChange}
                value={draft.category ?? ''}
              />
            </div>
          )}
          {/* Keywords Section - Only show for Caption Search */}
          {draft.captionSearch && (
            <div className={classes.keywordsSection}>
              <label className={classes.keywordsLabel}>
                Keyword to track in captions{' '}
                <span className={classes.required}>*</span>
              </label>
              <div className={classes.keywordsDisplay}>
                {draft.captionSearch.keyword
                  .split(', ')
                  .filter((k) => k.trim())
                  .map((keyword) => (
                    <div className={classes.keywordTag} key={keyword}>
                      #{keyword}
                    </div>
                  ))}
              </div>
            </div>
          )}
          {/* Recent Posts Section */}
          {draft.recentPosts && (
            <div className={classes.section}>
              <div className={classes.sectionHeader}>
                <div className={classes.sectionTitle}>
                  <svg fill="none" height="20" viewBox="0 0 24 24" width="20">
                    <path
                      d="M12 8V12L15 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    />
                  </svg>
                  <span>Recent Posts</span>
                </div>
                <div className={classes.sectionActions}>
                  <button
                    className={classes.editButton}
                    onClick={() => onEdit('recentPosts')}
                    type="button"
                  >
                    Edit
                  </button>
                  <button
                    className={classes.clearButton}
                    onClick={() => onClear('recentPosts')}
                    type="button"
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div className={classes.sectionContent}>
                <div className={classes.stat}>
                  <span className={classes.statLabel}>Posts to analyze:</span>
                  <span className={classes.statValue}>
                    {draft.recentPosts.postCount}
                  </span>
                </div>
                <div className={classes.urlsList}>
                  {draft.recentPosts.urls
                    .filter((u) => u.link)
                    .map((url) => (
                      <div className={classes.urlItem} key={url.id}>
                        {getPlatformIcon(url.platform)}
                        <span className={classes.urlText}>{url.link}</span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
          {/* Specific Posts Section */}
          {draft.specificPosts && (
            <div className={classes.section}>
              <div className={classes.sectionHeader}>
                <div className={classes.sectionTitle}>
                  <svg fill="none" height="20" viewBox="0 0 24 24" width="20">
                    <circle
                      cx="12"
                      cy="12"
                      r="3"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <circle
                      cx="12"
                      cy="12"
                      r="9"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                  <span>Specific Posts</span>
                </div>
                <div className={classes.sectionActions}>
                  <button
                    className={classes.editButton}
                    onClick={() => onEdit('specificPosts')}
                    type="button"
                  >
                    Edit
                  </button>
                  <button
                    className={classes.clearButton}
                    onClick={() => onClear('specificPosts')}
                    type="button"
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div className={classes.sectionContent}>
                <div className={classes.urlsList}>
                  {draft.specificPosts.urls
                    .filter((u) => u.link)
                    .map((url) => (
                      <div className={classes.urlItem} key={url.id}>
                        {getPlatformIcon(url.platform)}
                        <span className={classes.urlText}>{url.link}</span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
          {/* Caption Search Section */}
          {draft.captionSearch && (
            <div className={classes.section}>
              <div className={classes.sectionHeader}>
                <div className={classes.sectionTitle}>
                  <svg fill="none" height="20" viewBox="0 0 24 24" width="20">
                    <path d="M7 20V4H4V20H7Z" fill="currentColor" />
                    <path d="M14 20V4H11V20H14Z" fill="currentColor" />
                    <path d="M17 4L21 20H18L14 4H17Z" fill="currentColor" />
                  </svg>
                  <span>Keyword Search</span>
                </div>
                <div className={classes.sectionActions}>
                  <button
                    className={classes.editButton}
                    onClick={() => onEdit('captionSearch')}
                    type="button"
                  >
                    Edit
                  </button>
                  <button
                    className={classes.clearButton}
                    onClick={() => onClear('captionSearch')}
                    type="button"
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div className={classes.sectionContent}>
                <div className={classes.stat}>
                  <span className={classes.statLabel}>Keyword:</span>
                  <span className={classes.statValue}>
                    {draft.captionSearch.keyword}
                  </span>
                </div>
                <div className={classes.urlsList}>
                  {draft.captionSearch.urls
                    .filter((u) => u.link)
                    .map((url) => (
                      <div className={classes.urlItem} key={url.id}>
                        {getPlatformIcon(url.platform)}
                        <span className={classes.urlText}>{url.link}</span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className={classes.modalFooter}>
          <button
            className={classes.clearAllButton}
            disabled={!hasAnyData}
            onClick={onClearAll}
            type="button"
          >
            Clear All Links
          </button>
          <div className={classes.footerRight}>
            <button
              className={classes.cancelButton}
              onClick={onClose}
              type="button"
            >
              Cancel
            </button>
            <button
              className={classes.submitButton}
              disabled={!canSubmit}
              onClick={onSubmit}
              type="button"
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
